/**
 * Pure helpers for chat-style (WhatsApp/Telegram) message paging.
 *
 * Messages are fetched newest-first (`order: "desc"`) so the first page holds
 * the most recent messages. For display we render them ascending (oldest at the
 * top of the loaded window, newest at the bottom), and prepend older pages as
 * the user scrolls up.
 */

/** Reverse a newest-first page into ascending (oldest-first) display order. Never mutates the input. */
export function reverseToAscending<T>(descPage: readonly T[]): T[] {
  return [...descPage].reverse();
}

/**
 * Prepend an older, newest-first page before the current ascending list.
 * The older page is reversed to ascending, and any message whose id already
 * exists in the current list is dropped (de-dupe by id, keeping the current
 * copy). Never mutates either input.
 */
export function prependOlderPage<T extends { id: string }>(
  current: readonly T[],
  olderDesc: readonly T[],
): T[] {
  const seen = new Set(current.map((m) => m.id));
  const olderAsc = reverseToAscending(olderDesc).filter((m) => !seen.has(m.id));
  return [...olderAsc, ...current];
}
